import { createLogger } from './logger';

const logger = createLogger('API');

/**
 * 标准 API 响应结构
 */
export interface ApiResponse<T = any> {
  status: 'success' | 'fail' | 'error';
  data?: T;
  message?: string;
  code?: string;
  timestamp?: string;
  trace_id?: string;
}

/**
 * 断言 API 响应成功
 * 如果响应状态不是 'success'，抛出包含错误信息的 Error
 *
 * @param response API 响应对象
 * @param fallbackMessage 默认错误信息
 * @throws Error 当 response.status !== 'success' 时
 *
 * @example
 * const response = await request('/api/users');
 * assertSuccess(response, '获取用户列表失败');
 * // 如果失败会抛出 Error，成功则继续执行
 */
export function assertSuccess(
  response: any,
  fallbackMessage = '操作失败',
): void {
  if (response?.status !== 'success') {
    const errorMessage = response?.message || fallbackMessage;
    logger.error(errorMessage);
    throw new Error(errorMessage);
  }
}

/**
 * 统一处理 API 错误
 * 根据 HTTP 状态码返回用户友好的错误信息
 *
 * @param error Axios/umi-request 错误对象
 * @param context 错误上下文描述（如 "获取用户列表"）
 * @throws Error 总是抛出错误
 *
 * @example
 * try {
 *   const response = await request('/api/users');
 *   assertSuccess(response);
 *   return response.data;
 * } catch (error) {
 *   handleApiError(error, '获取用户列表失败');
 * }
 */
export function handleApiError(error: any, context: string): never {
  const status = error.response?.status;
  const errorData = error.response?.data;
  const detail = errorData?.detail;

  let errorMessage: string;

  switch (status) {
    case 401:
      errorMessage = '认证失败，请检查登录状态';
      break;
    case 403:
      errorMessage = '权限不足，无法执行此操作';
      break;
    case 404:
      errorMessage = '请求的资源不存在';
      break;
    case 409:
      errorMessage = errorData?.message || '资源冲突';
      break;
    case 422:
      // 处理验证错误
      if (Array.isArray(detail)) {
        errorMessage = detail
          .map((err: any) => {
            const field = err.loc?.join('.') || err.field || '';
            const msg = err.msg || err.message || '';
            return field ? `${field}: ${msg}` : msg;
          })
          .join('; ');
      } else if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (detail?.message) {
        errorMessage = detail.message;
      } else if (errorData?.message) {
        errorMessage = errorData.message;
      } else {
        errorMessage = '数据验证失败';
      }
      break;
    case 500:
      errorMessage = '服务器内部错误，请稍后重试';
      break;
    default:
      // 尝试从多个字段获取错误信息
      errorMessage =
        errorData?.message || errorData?.detail || error.message || context;
  }

  logger.error(`${context}: ${errorMessage}`, { status, error });
  throw new Error(errorMessage);
}

/**
 * 安全地执行 API 调用并处理错误
 * 封装 try-catch 逻辑，简化 service 函数
 *
 * @param apiCall 返回 Promise 的 API 调用函数
 * @param context 错误上下文描述
 * @returns API 响应数据
 *
 * @example
 * export async function getUsers() {
 *   return safeApiCall(
 *     () => request('/api/users'),
 *     '获取用户列表失败'
 *   );
 * }
 */
export async function safeApiCall<T>(
  apiCall: () => Promise<ApiResponse<T>>,
  context: string,
): Promise<T> {
  try {
    const response = await apiCall();
    assertSuccess(response, context);
    return response.data as T;
  } catch (error: any) {
    // 如果错误已经是我们抛出的，直接重新抛出
    if (error.message && !error.response) {
      throw error;
    }
    handleApiError(error, context);
  }
}
